import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { escapeFdaRecallNumberSearchFragment } from '../searchParamBuilders';
import {
  useCpscRecalls,
  useFdaEnforcementRecalls,
  useUsdaRecalls,
  useVehicleRecalls,
} from '../useRecallsApi';
import { USDA_TRANSLATION_LANGUAGE_IDS } from '../useRecallsApi/constants';
import {
  cpscRecordsToRecallItems,
  fdaRecordsToRecallItems,
  usdaRecordsToRecallItems,
  vehicleRecallsToRecallItems,
} from '../useRecallsApi/utils';
import { normalizeRecallForCard, str } from '../utils';

import type {
  CpscRecallParams,
  FdaEnforcementDomain,
  FdaEnforcementParams,
  NormalizedRecallCard,
  RecallItem,
  RecallSource,
  RecallType,
  UsdaRecallParams,
  VehicleRecallsParams,
} from '../interfaces';

const SOURCE_TO_RECALL_TYPE: Record<RecallSource, RecallType> = {
  fdaDrug: 'drugs',
  fdaDevice: 'medical devices',
  fdaFood: 'food',
  usda: 'food',
  cpsc: 'consumer product',
  vehicle: 'vehicle',
};

const SOURCE_TO_FDA_DOMAIN: Partial<
  Record<RecallSource, FdaEnforcementDomain>
> = {
  fdaDrug: 'drug',
  fdaDevice: 'device',
  fdaFood: 'food',
};

const VALID_SOURCES = new Set<string>(Object.keys(SOURCE_TO_RECALL_TYPE));

function parseVehicleParams(sp: URLSearchParams): VehicleRecallsParams {
  return {
    make: sp.get('make') ?? undefined,
    model: sp.get('model') ?? undefined,
    year: sp.get('year') ?? undefined,
    trim: sp.get('trim') ?? undefined,
    vin: sp.get('vin') ?? undefined,
    license_plate: sp.get('license_plate') ?? undefined,
    state: sp.get('state') ?? undefined,
  };
}

function findVehicleMatchIndex(items: RecallItem[], idFromUrl: string): number {
  const id = str(idFromUrl);
  if (!id) {
    return -1;
  }
  return items.findIndex(
    (item) =>
      item.source === 'vehicle' &&
      (str(item.record.oem_recall_number) === id ||
        str(item.record.campaign) === id)
  );
}

function findMatchAndSimilar(
  items: RecallItem[],
  isVehicle: boolean,
  oemRecallNumber: string
): { matchItem: RecallItem | null; similarCards: NormalizedRecallCard[] } {
  if (items.length === 0) {
    return { matchItem: null, similarCards: [] };
  }

  let matchIndex: number;
  if (isVehicle) {
    const vehicleId = str(oemRecallNumber);
    matchIndex = vehicleId ? findVehicleMatchIndex(items, oemRecallNumber) : 0;
  } else {
    matchIndex = 0;
  }

  const matched = matchIndex === -1 ? null : items[matchIndex];
  const otherItems =
    matchIndex === -1 ? items : items.filter((_, i) => i !== matchIndex);

  return {
    matchItem: matched,
    similarCards: otherItems.map(normalizeRecallForCard).slice(0, 3),
  };
}

export function useRecallReport() {
  const [searchParams] = useSearchParams();

  const source = searchParams.get('source') ?? '';
  const recallNumber = searchParams.get('recall_number') ?? '';
  const oemRecallNumber = searchParams.get('oem_recall_number') ?? '';

  const validSource = VALID_SOURCES.has(source)
    ? (source as RecallSource)
    : null;
  const recallType = validSource ? SOURCE_TO_RECALL_TYPE[validSource] : null;

  const isVehicle = validSource === 'vehicle';
  const fdaDomain = validSource ? SOURCE_TO_FDA_DOMAIN[validSource] : undefined;
  const isFda = !!fdaDomain;
  const isUsda = validSource === 'usda';
  const isCpsc = validSource === 'cpsc';

  const vehicleParams = useMemo<VehicleRecallsParams | null>(
    () => (isVehicle ? parseVehicleParams(searchParams) : null),
    [isVehicle, searchParams]
  );

  const fdaParams = useMemo<FdaEnforcementParams | null>(
    () =>
      isFda && fdaDomain && recallNumber
        ? {
            domain: fdaDomain,
            searchParams: {
              search: `recall_number:"${escapeFdaRecallNumberSearchFragment(
                recallNumber
              )}"`,
              limit: 10,
            },
          }
        : null,
    [isFda, fdaDomain, recallNumber]
  );

  const usdaParams = useMemo<UsdaRecallParams | null>(
    () =>
      isUsda && recallNumber
        ? {
            field_translation_language: USDA_TRANSLATION_LANGUAGE_IDS.English,
            field_recall_number: recallNumber,
          }
        : null,
    [isUsda, recallNumber]
  );

  const cpscParams = useMemo<CpscRecallParams | null>(
    () => (isCpsc && recallNumber ? { RecallNumber: recallNumber } : null),
    [isCpsc, recallNumber]
  );

  const { data: vehicleData } = useVehicleRecalls(vehicleParams, {
    enabled: isVehicle,
  });
  const { data: fdaData } = useFdaEnforcementRecalls(fdaParams, {
    enabled: isFda && !!recallNumber,
  });
  const { data: usdaData } = useUsdaRecalls(usdaParams, {
    enabled: isUsda && !!recallNumber,
  });
  const { data: cpscData } = useCpscRecalls(cpscParams, {
    enabled: isCpsc && !!recallNumber,
  });

  const items = useMemo<RecallItem[]>(() => {
    if (isVehicle) {
      return vehicleRecallsToRecallItems(vehicleData?.recalls ?? []);
    }
    if (isFda) {
      return fdaRecordsToRecallItems(
        fdaData,
        validSource as 'fdaDrug' | 'fdaDevice' | 'fdaFood'
      );
    }
    if (isUsda) {
      return usdaRecordsToRecallItems(usdaData);
    }
    if (isCpsc) {
      return cpscRecordsToRecallItems(cpscData);
    }
    return [];
  }, [
    isVehicle,
    isFda,
    isUsda,
    isCpsc,
    vehicleData,
    fdaData,
    usdaData,
    cpscData,
    validSource,
  ]);

  const { matchItem, similarCards } = useMemo(
    () => findMatchAndSimilar(items, isVehicle, oemRecallNumber),
    [items, isVehicle, oemRecallNumber]
  );

  return {
    validSource,
    recallType,
    matchItem,
    similarCards,
    vehicleParams,
  };
}
